import React from "react";
import { motion } from "framer-motion";
import { MANIFESTO } from "@/constants/testIds";

const chapters = [
  {
    n: "01",
    title: "Roots that remember",
    body: "Build unlimited family trees. Multi-generational, matrilineal, adopted, chosen — every kind of belonging fits here.",
    tag: "Family Tree Builder",
  },
  {
    n: "02",
    title: "Stories that survive",
    body: "Record oral histories, letters, recipes and diaries. Preserve grandmother's voice before it becomes a rumour.",
    tag: "Memory Vault",
  },
  {
    n: "03",
    title: "A village on a map",
    body: "Pin ancestral homes, temples, gurudwaras, mosques and cemeteries. Trace old district boundaries and migration routes.",
    tag: "Heritage Maps",
  },
  {
    n: "04",
    title: "Names with meaning",
    body: "Research surnames, gotras, kulams, kuladevatas and traditional occupations across regions of India.",
    tag: "Surnames & Gotras",
  },
  {
    n: "05",
    title: "One family, many shores",
    body: "Visualise migration — Village to city, India to UK, US, Canada, Australia, East Africa and the Gulf.",
    tag: "Diaspora Timelines",
  },
  {
    n: "06",
    title: "The network of memory",
    body: "IndianFamilyTree is part of the SocialPulse network — your genealogy connects to places, events and communities.",
    tag: "SocialPulse Network",
  },
];

export default function Manifesto() {
  return (
    <section
      id="manifesto"
      data-testid={MANIFESTO.root}
      className="relative py-28 md:py-40"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-4">
          <div className="sticky top-32">
            <span className="text-xs uppercase tracking-[0.3em] text-saffron">A manifesto</span>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink leading-[0.95]">
              Every family
              <span className="italic text-tulsi"> deserves </span>
              more than a list of names.
            </h2>
            <p className="mt-6 text-ink-50 text-lg font-light max-w-md leading-relaxed">
              Six chapters that shape how we build. Slow craft over quick features. A tharavad, not a spreadsheet.
            </p>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-8 lg:pl-16">
          <ol className="space-y-14">
            {chapters.map((c, i) => (
              <motion.li
                key={c.n}
                data-testid={MANIFESTO.chapter(i)}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                className="grid grid-cols-12 gap-4 border-b border-ivory-300 pb-14 last:border-none"
              >
                <div className="col-span-2 md:col-span-1">
                  <div className="font-serif text-2xl md:text-3xl text-saffron">{c.n}</div>
                </div>
                <div className="col-span-10 md:col-span-11">
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] tracking-[0.28em] uppercase text-ink-50">{c.tag}</span>
                    <span className="h-px flex-1 bg-ivory-300" />
                  </div>
                  <h3 className="mt-3 font-serif text-3xl md:text-4xl tracking-tight text-ink leading-tight">
                    {c.title}
                  </h3>
                  <p className="mt-3 text-ink-50 text-base md:text-lg font-light max-w-2xl leading-relaxed">
                    {c.body}
                  </p>
                </div>
              </motion.li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}
